// purchaseOrderItemsImportUtils.js
//
// Denormalized CSV → PROJECT / PURCHASE_ORDER / PURCHASE_ORDER_ITEM (+ PRODUCT / VENDOR)
// reshape + upsert planning for Project-scoped inventory import.

import Papa from 'papaparse';
import { MAX_IMPORT_ROWS, parseCsvFile } from 'collection/import/importCsvUtils';
import { readNodeFieldRawValue } from 'utilities/NodeFieldData';

export { MAX_IMPORT_ROWS, parseCsvFile };

export const PRODUCT_TYPE_KEY = 'PRODUCT';
export const VENDOR_TYPE_KEY = 'VENDOR';
export const PURCHASE_ORDER_TYPE_KEY = 'PURCHASE_ORDER';
export const PURCHASE_ORDER_ITEM_TYPE_KEY = 'PURCHASE_ORDER_ITEM';
export const PROJECT_TYPE_KEY = 'PROJECT';

export const IMPORT_PURCHASE_ORDER_ITEMS_ACTION = '__import_purchase_order_items__';

const UNNUMBERED_PO_TOKEN = '__UNNUMBERED__';
const NO_VENDOR_TOKEN = '__NO_VENDOR__';

const PRODUCT_DATA = 'data_product';
const VENDOR_DATA = 'data_vendor';
const PO_DATA = 'data_purchase_order';
const PO_ITEM_DATA = 'data_purchase_order_item';

const TRUTHY_VALUES = new Set(['true', '1', 'yes', 'y']);
const FALSY_VALUES = new Set(['false', '0', 'no', 'n']);

/**
 * Curated template columns for the denormalized PO-item import.
 * Order matters — this is the downloadable CSV header row.
 */
export const PURCHASE_ORDER_ITEMS_IMPORT_COLUMNS = Object.freeze([
  Object.freeze({
    key: 'title',
    header: 'Title',
    required: true,
    aliases: ['title', 'name', 'item title', 'item name', 'product'],
    example: 'Walnut dining chair',
  }),
  Object.freeze({
    key: 'poNumber',
    header: 'PO Number',
    aliases: ['po number', 'purchase order number', 'po #', 'po#', 'ponumber', 'number'],
    example: '400297',
  }),
  Object.freeze({
    key: 'vendor',
    header: 'Vendor',
    aliases: ['vendor', 'vendor name', 'supplier'],
    example: 'Design Within Reach',
  }),
  Object.freeze({
    key: 'sku',
    header: 'SKU',
    aliases: ['sku', 'item sku', 'product sku'],
    example: 'DWR-CHAIR-01',
  }),
  Object.freeze({
    key: 'quantity',
    header: 'Quantity',
    aliases: ['quantity', 'qty', 'ordered quantity', 'ordered qty'],
    example: '2',
  }),
  Object.freeze({
    key: 'status',
    header: 'Status',
    aliases: ['status', 'item status', 'fulfillment status'],
    example: 'ordered',
  }),
  Object.freeze({
    key: 'category',
    header: 'Category',
    aliases: ['category'],
    example: 'Seating',
  }),
  Object.freeze({
    key: 'room',
    header: 'Room',
    aliases: ['room', 'area', 'space'],
    example: 'Dining Room',
  }),
  Object.freeze({
    key: 'division',
    header: 'Division',
    aliases: ['division', 'department'],
    example: '',
  }),
  Object.freeze({
    key: 'currentLocation',
    header: 'Current Location',
    aliases: ['current location', 'location'],
    example: 'Warehouse',
  }),
  Object.freeze({
    key: 'damaged',
    header: 'Damaged',
    aliases: ['damaged'],
    example: 'No',
  }),
  Object.freeze({
    key: 'eta',
    header: 'ETA',
    aliases: ['eta', 'expected arrival', 'expected arrival date'],
    example: '2026-04-15',
  }),
  Object.freeze({
    key: 'orderedDate',
    header: 'Ordered Date',
    aliases: ['ordered date', 'ordered', 'date ordered'],
    example: '2026-03-01',
  }),
  Object.freeze({
    key: 'shippedDate',
    header: 'Shipped Date',
    aliases: ['shipped date', 'shipped', 'date shipped'],
    example: '',
  }),
  Object.freeze({
    key: 'arrivedDate',
    header: 'Arrived Date',
    aliases: ['arrived date', 'arrived', 'date arrived'],
    example: '',
  }),
  Object.freeze({
    key: 'deliveredDate',
    header: 'Delivered Date',
    aliases: ['delivered date', 'delivered', 'date delivered'],
    example: '',
  }),
]);

/**
 * @param {unknown} value
 * @returns {string}
 */
export function cleanString(value) {
  return String(value || '').trim();
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeMatchToken(value) {
  return cleanString(value).toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeHeaderToken(value) {
  return cleanString(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * @param {unknown} typeKey
 * @returns {boolean}
 */
export function isProjectTypeKey(typeKey) {
  return cleanString(typeKey).toUpperCase() === PROJECT_TYPE_KEY;
}

/**
 * @param {object|null|undefined} node
 * @returns {boolean}
 */
export function isProjectNode(node) {
  return isProjectTypeKey(node?.type);
}

/**
 * Maps free-text inventory statuses onto PURCHASE_ORDER_ITEM status values.
 *
 * @param {unknown} raw
 * @returns {string}
 */
export function mapPurchaseOrderItemStatus(raw) {
  const value = normalizeHeaderToken(raw);
  if (!value) return 'pending';
  if (value === 'ordered') return 'ordered';
  if (value === 'shipped' || value === 'en route' || value === 'enroute' || value === 'in transit') {
    return 'en_route';
  }
  if (value === 'arrived') return 'arrived';
  if (value === 'delivered') return 'delivered';
  if (value === 'cancelled' || value === 'canceled') return 'cancelled';
  if (value === 'pending' || value === 'not ordered') return 'pending';
  if (value === 'en_route') return 'en_route';
  return 'pending';
}

/**
 * @param {unknown} raw
 * @returns {{ ok: true, value: boolean|null } | { ok: false, reason: string }}
 */
function parseBooleanCell(raw) {
  const value = cleanString(raw);
  if (!value) return { ok: true, value: null };
  const normalized = value.toLowerCase();
  if (TRUTHY_VALUES.has(normalized)) return { ok: true, value: true };
  if (FALSY_VALUES.has(normalized)) return { ok: true, value: false };
  return { ok: false, reason: `Expected Yes/No for Damaged (got "${value}").` };
}

/**
 * @param {unknown} raw
 * @returns {{ ok: true, value: string|null } | { ok: false, reason: string }}
 */
function parseDateCell(raw) {
  const value = cleanString(raw);
  if (!value) return { ok: true, value: null };
  const asDate = new Date(value);
  if (Number.isNaN(asDate.getTime())) {
    return { ok: false, reason: `Invalid date "${value}".` };
  }
  return { ok: true, value: asDate.toISOString() };
}

/**
 * @param {unknown} raw
 * @returns {{ ok: true, value: number } | { ok: false, reason: string }}
 */
function parseQuantityCell(raw) {
  const value = cleanString(raw);
  if (!value) return { ok: true, value: 1 };
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return { ok: false, reason: `Expected a positive Quantity (got "${value}").` };
  }
  return { ok: true, value: numeric };
}

/**
 * Builds the downloadable template CSV (header + one example row).
 *
 * @returns {string}
 */
export function buildPurchaseOrderItemsImportTemplateCsv() {
  const headers = PURCHASE_ORDER_ITEMS_IMPORT_COLUMNS.map((column) => column.header);
  const exampleRow = PURCHASE_ORDER_ITEMS_IMPORT_COLUMNS.map((column) => column.example || '');
  return Papa.unparse({
    fields: headers,
    data: [exampleRow],
  });
}

/**
 * Maps CSV headers onto curated column keys.
 *
 * @param {string[]} headers
 * @returns {{ mappings: Record<string, string|null>, ignoredHeaders: string[] }}
 */
export function buildPurchaseOrderItemsImportMappings(headers = []) {
  const aliasToKey = new Map();
  PURCHASE_ORDER_ITEMS_IMPORT_COLUMNS.forEach((column) => {
    aliasToKey.set(normalizeHeaderToken(column.header), column.key);
    (column.aliases || []).forEach((alias) => {
      aliasToKey.set(normalizeHeaderToken(alias), column.key);
    });
  });

  const mappings = {};
  const ignoredHeaders = [];
  const usedKeys = new Set();

  (Array.isArray(headers) ? headers : []).forEach((header) => {
    const token = normalizeHeaderToken(header);
    const key = aliasToKey.get(token) || null;
    if (!key || usedKeys.has(key)) {
      mappings[header] = null;
      ignoredHeaders.push(header);
      return;
    }
    usedKeys.add(key);
    mappings[header] = key;
  });

  return { mappings, ignoredHeaders };
}

/**
 * @param {object} node
 * @param {string} fieldName
 * @param {string} typeKey
 * @param {string} sectionKey
 * @returns {unknown}
 */
function readTypedField(node, fieldName, typeKey, sectionKey) {
  if (!node || !fieldName) return undefined;
  const fromHelper = readNodeFieldRawValue(
    node,
    { name: fieldName, source: typeKey.toLowerCase() },
    typeKey,
  );
  if (fromHelper !== undefined) return fromHelper;
  const section = node?.[sectionKey];
  if (section && typeof section === 'object' && fieldName in section) {
    return section[fieldName];
  }
  return undefined;
}

/**
 * @param {unknown} value
 * @returns {string[]}
 */
export function normalizeRelationKeys(value) {
  if (Array.isArray(value)) {
    return value.map((entry) => cleanString(entry)).filter(Boolean);
  }
  const single = cleanString(value);
  return single ? [single] : [];
}

/**
 * @param {object|null|undefined} purchaseOrder
 * @returns {string}
 */
export function readPurchaseOrderNumber(purchaseOrder) {
  return cleanString(
    readTypedField(purchaseOrder, 'number', PURCHASE_ORDER_TYPE_KEY, PO_DATA)
    || readTypedField(purchaseOrder, 'poNumber', PURCHASE_ORDER_TYPE_KEY, PO_DATA),
  );
}

/**
 * @param {object|null|undefined} purchaseOrder
 * @returns {string}
 */
export function readPurchaseOrderVendorKey(purchaseOrder) {
  const keys = normalizeRelationKeys(
    readTypedField(purchaseOrder, 'vendor', PURCHASE_ORDER_TYPE_KEY, PO_DATA),
  );
  return keys[0] || '';
}

/**
 * @param {object|null|undefined} product
 * @returns {string}
 */
export function readProductSku(product) {
  return cleanString(readTypedField(product, 'sku', PRODUCT_TYPE_KEY, PRODUCT_DATA));
}

/**
 * @param {object|null|undefined} item
 * @returns {string}
 */
export function readPurchaseOrderItemSku(item) {
  return cleanString(
    readTypedField(item, 'sku', PURCHASE_ORDER_ITEM_TYPE_KEY, PO_ITEM_DATA),
  );
}

/**
 * @param {object|null|undefined} item
 * @returns {string}
 */
export function readPurchaseOrderItemProductKey(item) {
  const keys = normalizeRelationKeys(
    readTypedField(item, 'product', PURCHASE_ORDER_ITEM_TYPE_KEY, PO_ITEM_DATA),
  );
  return keys[0] || '';
}

/**
 * @param {object} options
 * @param {string} options.poNumber
 * @param {string} options.vendorToken
 * @returns {string}
 */
export function buildPoGroupKey({ poNumber, vendorToken }) {
  const numberToken = cleanString(poNumber) || UNNUMBERED_PO_TOKEN;
  const vendor = cleanString(vendorToken) || NO_VENDOR_TOKEN;
  return `${numberToken}::${vendor}`;
}

/**
 * @param {string} poNumber
 * @returns {string}
 */
export function buildPurchaseOrderTitle(poNumber) {
  const number = cleanString(poNumber);
  if (!number) return 'Unnumbered PO';
  if (/^po[\s-]*/i.test(number)) return number;
  return `PO ${number}`;
}

/**
 * Parses CSV rows into typed import rows using curated mappings.
 *
 * @param {object} options
 * @param {object[]} options.rows
 * @param {string[]} options.headers
 * @param {Record<string, string|null>} options.mappings
 * @returns {{ validRows: object[], skippedRows: object[] }}
 */
export function parsePurchaseOrderItemsImportRows({
  rows = [],
  headers = [],
  mappings = {},
} = {}) {
  const validRows = [];
  const skippedRows = [];

  (Array.isArray(rows) ? rows : []).forEach((row, index) => {
    const rowNumber = index + 2;
    const errors = [];
    const values = {
      title: '',
      poNumber: '',
      vendor: '',
      sku: '',
      quantity: 1,
      status: 'pending',
      category: '',
      room: '',
      division: '',
      currentLocation: '',
      damaged: null,
      eta: null,
      orderedDate: null,
      shippedDate: null,
      arrivedDate: null,
      deliveredDate: null,
    };

    (Array.isArray(headers) ? headers : []).forEach((header) => {
      const key = mappings?.[header];
      if (!key) return;
      const raw = row?.[header];

      if (key === 'title') {
        values.title = cleanString(raw);
        return;
      }
      if (key === 'poNumber') {
        values.poNumber = cleanString(raw);
        return;
      }
      if (key === 'vendor') {
        values.vendor = cleanString(raw);
        return;
      }
      if (key === 'sku') {
        values.sku = cleanString(raw);
        return;
      }
      if (key === 'category') {
        values.category = cleanString(raw);
        return;
      }
      if (key === 'room') {
        values.room = cleanString(raw);
        return;
      }
      if (key === 'division') {
        values.division = cleanString(raw);
        return;
      }
      if (key === 'currentLocation') {
        values.currentLocation = cleanString(raw);
        return;
      }
      if (key === 'status') {
        values.status = mapPurchaseOrderItemStatus(raw);
        return;
      }
      if (key === 'quantity') {
        const parsed = parseQuantityCell(raw);
        if (!parsed.ok) {
          errors.push(parsed.reason);
          return;
        }
        values.quantity = parsed.value;
        return;
      }
      if (key === 'damaged') {
        const parsed = parseBooleanCell(raw);
        if (!parsed.ok) {
          errors.push(parsed.reason);
          return;
        }
        values.damaged = parsed.value;
        return;
      }
      if (
        key === 'eta'
        || key === 'orderedDate'
        || key === 'shippedDate'
        || key === 'arrivedDate'
        || key === 'deliveredDate'
      ) {
        const parsed = parseDateCell(raw);
        if (!parsed.ok) {
          errors.push(parsed.reason);
          return;
        }
        values[key] = parsed.value;
      }
    });

    if (!values.title) {
      errors.push('Title is required.');
    }

    const parsedRow = {
      rowNumber,
      ...values,
      errors,
      isValid: errors.length === 0,
    };

    if (parsedRow.isValid) validRows.push(parsedRow);
    else skippedRows.push(parsedRow);
  });

  return { validRows, skippedRows };
}

/**
 * @param {object[]} nodes
 * @param {(node: object) => string} getToken
 * @returns {Map<string, object[]>}
 */
function indexNodesByToken(nodes, getToken) {
  const map = new Map();
  (Array.isArray(nodes) ? nodes : []).forEach((node) => {
    const token = cleanString(getToken(node));
    if (!token) return;
    const list = map.get(token) || [];
    list.push(node);
    map.set(token, list);
  });
  return map;
}

/**
 * @param {Map<string, object[]>} index
 * @param {string} token
 * @returns {{ match: object|null, ambiguous: boolean }}
 */
function resolveUniqueMatch(index, token) {
  const key = cleanString(token);
  if (!key) return { match: null, ambiguous: false };
  const matches = index.get(key) || [];
  if (matches.length === 1) return { match: matches[0], ambiguous: false };
  if (matches.length > 1) return { match: null, ambiguous: true };
  return { match: null, ambiguous: false };
}

/**
 * Builds an upsert plan from parsed rows + existing Project graph.
 *
 * @param {object} options
 * @param {object[]} options.validRows
 * @param {object[]} [options.purchaseOrders]
 * @param {Record<string, object[]>|Map<string, object[]>} [options.itemsByPoKey]
 * @param {object[]} [options.products]
 * @param {object[]} [options.vendors]
 * @returns {object}
 */
export function buildPurchaseOrderItemsImportPlan({
  validRows = [],
  purchaseOrders = [],
  itemsByPoKey = {},
  products = [],
  vendors = [],
} = {}) {
  const vendorByTitle = indexNodesByToken(
    vendors,
    (node) => normalizeMatchToken(node?.title),
  );
  const productBySku = indexNodesByToken(
    products,
    (node) => normalizeMatchToken(readProductSku(node)),
  );
  const productByTitle = indexNodesByToken(
    products,
    (node) => normalizeMatchToken(node?.title),
  );

  const poByGroupKey = new Map();
  const registerPoGroup = (po, poNumber, vendorToken) => {
    const groupKey = buildPoGroupKey({
      poNumber: poNumber || UNNUMBERED_PO_TOKEN,
      vendorToken: vendorToken || NO_VENDOR_TOKEN,
    });
    if (!poByGroupKey.has(groupKey)) {
      poByGroupKey.set(groupKey, po);
    }
  };

  (Array.isArray(purchaseOrders) ? purchaseOrders : []).forEach((po) => {
    const number = readPurchaseOrderNumber(po);
    const vendorKey = readPurchaseOrderVendorKey(po);
    const vendorNode = (Array.isArray(vendors) ? vendors : []).find(
      (row) => cleanString(row?.key) === vendorKey,
    );
    const titleToken = normalizeMatchToken(vendorNode?.title) || NO_VENDOR_TOKEN;

    if (vendorKey) {
      registerPoGroup(po, number, `key:${vendorKey}`);
    }
    registerPoGroup(po, number, titleToken);
  });

  const itemsMap = itemsByPoKey instanceof Map
    ? itemsByPoKey
    : new Map(Object.entries(itemsByPoKey || {}));

  const vendorOps = new Map();
  const productOps = new Map();
  const poOps = new Map();
  const itemOps = [];
  const ambiguousRows = [];

  let nextTempId = 1;
  const allocTempId = (prefix) => `${prefix}_${nextTempId++}`;

  const ensureVendor = (vendorTitle) => {
    const title = cleanString(vendorTitle);
    if (!title) return null;

    const token = normalizeMatchToken(title);
    if (vendorOps.has(token)) return vendorOps.get(token);

    const existing = resolveUniqueMatch(vendorByTitle, token);
    if (existing.ambiguous) {
      return { action: 'ambiguous', title, token };
    }

    if (existing.match) {
      const op = {
        action: 'reuse',
        tempId: allocTempId('vendor'),
        key: existing.match.key,
        title: cleanString(existing.match.title) || title,
        token,
      };
      vendorOps.set(token, op);
      return op;
    }

    const op = {
      action: 'create',
      tempId: allocTempId('vendor'),
      key: null,
      title,
      token,
      data: {},
      sectionKey: VENDOR_DATA,
    };
    vendorOps.set(token, op);
    return op;
  };

  const ensureProduct = (row) => {
    const title = cleanString(row.title);
    const sku = cleanString(row.sku);
    const skuToken = normalizeMatchToken(sku);
    const titleToken = normalizeMatchToken(title);
    const cacheKey = skuToken ? `sku:${skuToken}` : `title:${titleToken}`;

    if (productOps.has(cacheKey)) return productOps.get(cacheKey);

    let match = null;
    let ambiguous = false;

    if (skuToken) {
      const bySku = resolveUniqueMatch(productBySku, skuToken);
      if (bySku.ambiguous) ambiguous = true;
      else match = bySku.match;
    }
    if (!match && !ambiguous && titleToken) {
      const byTitle = resolveUniqueMatch(productByTitle, titleToken);
      if (byTitle.ambiguous) ambiguous = true;
      else match = byTitle.match;
    }

    if (ambiguous) {
      return { action: 'ambiguous', title, sku };
    }

    const data = {};
    if (sku) data.sku = sku;
    if (row.category) data.category = row.category;

    if (match) {
      const op = {
        action: 'update',
        tempId: allocTempId('product'),
        key: match.key,
        title: cleanString(match.title) || title,
        data,
        sectionKey: PRODUCT_DATA,
      };
      productOps.set(cacheKey, op);
      return op;
    }

    const op = {
      action: 'create',
      tempId: allocTempId('product'),
      key: null,
      title,
      data,
      sectionKey: PRODUCT_DATA,
    };
    productOps.set(cacheKey, op);
    return op;
  };

  const ensurePurchaseOrder = (row, vendorOp) => {
    const poNumber = cleanString(row.poNumber);
    const vendorKey = vendorOp?.key || null;
    const vendorToken = vendorKey
      ? `key:${vendorKey}`
      : (normalizeMatchToken(row.vendor) || NO_VENDOR_TOKEN);
    const groupKey = buildPoGroupKey({
      poNumber: poNumber || UNNUMBERED_PO_TOKEN,
      vendorToken,
    });

    if (poOps.has(groupKey)) return poOps.get(groupKey);

    const existing = poByGroupKey.get(groupKey)
      || poByGroupKey.get(buildPoGroupKey({
        poNumber: poNumber || UNNUMBERED_PO_TOKEN,
        vendorToken: normalizeMatchToken(row.vendor) || NO_VENDOR_TOKEN,
      }))
      || null;

    const data = {};
    if (poNumber) data.number = poNumber;

    if (existing) {
      const op = {
        action: 'update',
        tempId: allocTempId('po'),
        key: existing.key,
        title: cleanString(existing.title) || buildPurchaseOrderTitle(poNumber),
        groupKey,
        vendorTempId: vendorOp?.tempId || null,
        vendorKey,
        data,
        sectionKey: PO_DATA,
      };
      poOps.set(groupKey, op);
      return op;
    }

    const op = {
      action: 'create',
      tempId: allocTempId('po'),
      key: null,
      title: buildPurchaseOrderTitle(poNumber),
      groupKey,
      vendorTempId: vendorOp?.tempId || null,
      vendorKey,
      data: {
        ...data,
        documentStatus: 'draft',
      },
      sectionKey: PO_DATA,
    };
    poOps.set(groupKey, op);
    return op;
  };

  const findExistingItem = (poKey, productKey, sku, title) => {
    const items = itemsMap.get(poKey) || [];
    if (!items.length) return { match: null, ambiguous: false };

    if (productKey) {
      const byProduct = items.filter(
        (item) => readPurchaseOrderItemProductKey(item) === productKey,
      );
      if (byProduct.length === 1) return { match: byProduct[0], ambiguous: false };
      if (byProduct.length > 1) return { match: null, ambiguous: true };
    }

    const skuToken = normalizeMatchToken(sku);
    if (skuToken) {
      const bySku = items.filter(
        (item) => normalizeMatchToken(readPurchaseOrderItemSku(item)) === skuToken,
      );
      if (bySku.length === 1) return { match: bySku[0], ambiguous: false };
      if (bySku.length > 1) return { match: null, ambiguous: true };
    }

    const titleToken = normalizeMatchToken(title);
    if (titleToken) {
      const byTitle = items.filter(
        (item) => normalizeMatchToken(item?.title) === titleToken,
      );
      if (byTitle.length === 1) return { match: byTitle[0], ambiguous: false };
      if (byTitle.length > 1) return { match: null, ambiguous: true };
    }

    return { match: null, ambiguous: false };
  };

  (Array.isArray(validRows) ? validRows : []).forEach((row) => {
    const vendorOp = ensureVendor(row.vendor);
    if (vendorOp?.action === 'ambiguous') {
      ambiguousRows.push({
        ...row,
        reason: `Multiple vendors match "${row.vendor}".`,
      });
      return;
    }

    const productOp = ensureProduct(row);
    if (productOp?.action === 'ambiguous') {
      ambiguousRows.push({
        ...row,
        reason: row.sku
          ? `Multiple products match SKU "${row.sku}".`
          : `Multiple products match title "${row.title}".`,
      });
      return;
    }

    const poOp = ensurePurchaseOrder(row, vendorOp);

    const itemData = {
      quantity: row.quantity,
      status: row.status,
    };
    if (row.sku) itemData.sku = row.sku;
    if (row.category) itemData.category = row.category;
    if (row.room) itemData.room = row.room;
    if (row.division) itemData.division = row.division;
    if (row.currentLocation) itemData.currentLocation = row.currentLocation;
    if (typeof row.damaged === 'boolean') itemData.damaged = row.damaged;
    if (row.eta) itemData.eta = row.eta;
    if (row.orderedDate) itemData.orderedDate = row.orderedDate;
    if (row.shippedDate) itemData.shippedDate = row.shippedDate;
    if (row.arrivedDate) itemData.arrivedDate = row.arrivedDate;
    if (row.deliveredDate) itemData.deliveredDate = row.deliveredDate;

    let existingItem = null;
    let ambiguous = false;

    if (poOp.key) {
      const resolved = findExistingItem(
        poOp.key,
        productOp.key,
        row.sku,
        row.title,
      );
      existingItem = resolved.match;
      ambiguous = resolved.ambiguous;
    }

    if (ambiguous) {
      ambiguousRows.push({
        ...row,
        reason: 'Multiple purchase order items match this row on the same PO.',
      });
      return;
    }

    if (existingItem) {
      itemOps.push({
        action: 'update',
        tempId: allocTempId('item'),
        key: existingItem.key,
        title: cleanString(existingItem.title) || row.title,
        rowNumber: row.rowNumber,
        poTempId: poOp.tempId,
        productTempId: productOp.tempId,
        vendorTempId: vendorOp?.tempId || null,
        data: itemData,
        sectionKey: PO_ITEM_DATA,
      });
      return;
    }

    itemOps.push({
      action: 'create',
      tempId: allocTempId('item'),
      key: null,
      title: row.title,
      rowNumber: row.rowNumber,
      poTempId: poOp.tempId,
      productTempId: productOp.tempId,
      vendorTempId: vendorOp?.tempId || null,
      data: itemData,
      sectionKey: PO_ITEM_DATA,
    });
  });

  const vendorList = Array.from(vendorOps.values()).filter(
    (op) => op.action === 'create' || op.action === 'reuse',
  );
  const productList = Array.from(productOps.values()).filter(
    (op) => op.action === 'create' || op.action === 'update',
  );
  const poList = Array.from(poOps.values());

  const countByAction = (list, action) => list.filter((op) => op.action === action).length;

  return {
    vendors: vendorList,
    products: productList,
    purchaseOrders: poList,
    items: itemOps,
    ambiguousRows,
    stats: {
      vendorsCreate: countByAction(vendorList, 'create'),
      vendorsReuse: countByAction(vendorList, 'reuse'),
      productsCreate: countByAction(productList, 'create'),
      productsUpdate: countByAction(productList, 'update'),
      purchaseOrdersCreate: countByAction(poList, 'create'),
      purchaseOrdersUpdate: countByAction(poList, 'update'),
      itemsCreate: countByAction(itemOps, 'create'),
      itemsUpdate: countByAction(itemOps, 'update'),
      ambiguousCount: ambiguousRows.length,
    },
  };
}

/**
 * Builds a full preview from parsed CSV + existing nodes.
 *
 * @param {object} options
 * @returns {object}
 */
export function buildPurchaseOrderItemsImportPreview({
  rows = [],
  headers = [],
  purchaseOrders = [],
  itemsByPoKey = {},
  products = [],
  vendors = [],
} = {}) {
  const { mappings, ignoredHeaders } = buildPurchaseOrderItemsImportMappings(headers);
  const { validRows, skippedRows } = parsePurchaseOrderItemsImportRows({
    rows,
    headers,
    mappings,
  });
  const plan = buildPurchaseOrderItemsImportPlan({
    validRows,
    purchaseOrders,
    itemsByPoKey,
    products,
    vendors,
  });

  return {
    mappings,
    ignoredHeaders,
    validRows,
    skippedRows,
    plan,
    stats: {
      rowCount: Array.isArray(rows) ? rows.length : 0,
      validCount: validRows.length,
      skippedCount: skippedRows.length,
      ignoredColumnCount: ignoredHeaders.length,
      ...plan.stats,
    },
  };
}
